import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { apiClient } from './api-client';
import { AuthContext, type AuthState } from './auth-context';
import { refreshSession, setAccessToken, type AdminProfile } from './session';

interface LoginResponse {
  accessToken: string;
  expiresIn: number;
  user: AdminProfile & { role: string };
}

/** Staff only — a customer signing in here would land in an app with no data. */
const STAFF_ROLES = new Set([
  'SUPER_ADMIN',
  'ADMIN',
  'SALES_MANAGER',
  'SALES_AGENT',
  'CATALOG_MANAGER',
  'INVENTORY_MANAGER',
  'FINANCE_MANAGER',
  'SUPPORT_AGENT',
]);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminProfile | null>(null);
  const [loading, setLoading] = useState(true);

  // On a cold load the access token is gone but the refresh cookie may not be;
  // trade it for a token and restore the session before rendering any route.
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const token = await refreshSession();

      if (cancelled) return;

      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const profile = await apiClient.get<AdminProfile>('/auth/me');
        if (!cancelled) setUser(profile);
      } catch {
        if (!cancelled) setAccessToken(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const result = await apiClient.post<LoginResponse>(
      '/auth/login',
      { email, password },
      { anonymous: true },
    );

    if (!STAFF_ROLES.has(result.user.role)) {
      // Do not keep a session the app cannot use.
      await apiClient.post('/auth/logout', undefined, { anonymous: true }).catch(() => undefined);
      throw new Error('This account does not have access to the admin panel.');
    }

    setAccessToken(result.accessToken);
    setUser(result.user);
  }, []);

  const signOut = useCallback(async () => {
    await apiClient.post('/auth/logout').catch(() => undefined);
    setAccessToken(null);
    setUser(null);
  }, []);

  const clearSession = useCallback(() => {
    setAccessToken(null);
    setUser(null);
  }, []);

  const updateUser = useCallback((profile: AdminProfile) => setUser(profile), []);

  const can = useCallback(
    (permission: string) =>
      user?.role === 'SUPER_ADMIN' || (user?.permissions.includes(permission) ?? false),
    [user],
  );

  const value = useMemo<AuthState>(
    () => ({ user, loading, signIn, signOut, clearSession, updateUser, can }),
    [user, loading, signIn, signOut, clearSession, updateUser, can],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
